import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import Hr from 'components/Common/Hr'
import Image from 'components/Common/Image'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import LineClockIcon from 'components/Luxkit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'components/Luxkit/Icons/line/LineGlobeIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import Pane from 'components/Common/Pane'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import { pluralizeToString } from 'lib/string/pluralize'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getCruiseCountriesByItinerary, getCruiseOfferPriceVariation, getCruisePortsByItinerary, offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import Pill from 'components/Luxkit/Pill'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import CruiseSupportButton from './CruiseSupportButton'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import CruiseInclusionList from 'components/Cruises/CruiseInclusion/CruiseInclusionList'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ModalContext from 'contexts/ModalContext'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import ContactUSP from 'components/OfferPage/ContactUSP'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import config from 'constants/config'
import TextLink from 'components/Luxkit/TextLink'
import qs from 'qs'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { usePlaceByName } from 'hooks/usePlace'

const Root = styled.div`
  display: grid;
  grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "title title ."
      ". . ."
      "details details details"
      ". . ."
      "top-support-banner top-support-banner top-support-banner"
      ". . ."
      "overview overview overview"
      ". . ."
      "promo promo promo"
      ". . ."
      "description description description"
      ". . ."
      "journey journey journey"
      ". . ."
      "bottom-support-banner bottom-support-banner bottom-support-banner"
      ;
  grid-template-columns: 1fr;
  grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(8)}
      auto
      ${rem(16)}
      auto
      auto
      auto
      auto
      auto
      auto;
  max-width: 100%;


  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "title . overview"
      ". . overview"
      "details . overview"
      ". . overview"
      "top-support-banner . overview"
      "promo . overview"
      "description . overview"
      ". . overview"
      "journey . overview"
      "bottom-support-banner . ."; /* to create a fake empty area to push content up */
    grid-template-columns: 1fr ${rem(32)} ${rem(400)};
    grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(8)}
      auto
      ${rem(16)}
      auto
      auto
      auto
      auto
      auto
      auto;
  }
`

const OverviewArea = styled.div`
  grid-area: overview;
`

const JourneyArea = styled.div`
  grid-area: journey;
`

const ContactUSPArea = styled.div`
  padding: ${rem(20)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  grid-area: top-support-banner;
`

const TopBarArea = styled.div`
  align-items: top;
  display: flex;
  gap: ${rem(16)};
  justify-content: space-between;
  flex-direction: column-reverse;
  grid-area: top-bar;

  & > div:last-child {
    justify-content: flex-end;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const TopBarFlex = styled.div`
  align-items: top;
  display: flex;
  gap: ${rem(16)};
`

const TitleArea = styled.div`
  grid-area: title;
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-self: center;
    flex-shrink: 0;
    max-height: ${rem(75)};
    width: ${rem(150)};
  }
`

const DetailItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding-top: ${rem(12)};
  grid-area: details;

  &:empty {
    display: none;
  }
`

const OverviewPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  box-shadow: ${props => props.theme.shadow.flat.small};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(20)};
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);

  &.three-columns {
    display: flex;
    gap: ${rem(24)};
  }
`

const OverviewPanePriceContainer = styled.div`
  display: flex;
  flex-direction: column;

  > :last-child {
    padding-top: ${rem(8)};
  }
`

const PromotionArea = styled.div`
  grid-area: promo;
  padding-top: ${rem(16)};
`

const ShipDescriptionArea = styled.div`
  grid-area: description;
  padding: ${rem(16)} 0;
`

export const TextLinkVendor = styled(TextLink)`
  padding-left: ${rem(4)};
`

interface Props {
  offer: App.CruiseOffer;
  shareModalConfig?: ShareConfig;
  showPackageOptions: () => void;
  departurePortPlace?: App.Place;
}

function CruiseOverview(props: Props) {
  const {
    shareModalConfig,
    offer,
    showPackageOptions,
    departurePortPlace,
  } = props
  const {
    destinationCity,
    standardInclusions,
    luxPlusInclusions,
  } = useCruiseOfferView({ offer })
  const destinationPlace = usePlaceByName(destinationCity)
  const isMobile = useScreenSizeOnly('mobile')

  const { departurePort, returnPort, name, ship, itinerary } = offer
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails
  const { promotion, discountPills } = lowestOverallPriceDetails
  const promotionDetails = promotion ?? offer.fallbackPromotion
  const showOverviewArea = !useScreenSizeOnly('mobile')
  const shipInfoDescription = ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
  const offerDescription = offer.description || (shipInfoDescription || offer.destinationDescription)?.replace(/(<([^>]+)>)/gi, '')

  // operator
  const operatorName = offer.cruiseLine.name
  const operatorLogo = offer.cruiseLine.imageId

  const hasTheSameStartAndEndPort = departurePort === returnPort

  const ports = useMemo(() => getCruisePortsByItinerary(itinerary), [itinerary])
  const countries = useMemo(() => getCruiseCountriesByItinerary(itinerary), [itinerary])

  const returnPortCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])
  const departurePortCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])

  const hasValidDepartureDates = useMemo(() => offerHasValidDepartures(offer), [offer])

  const {
    primaryPrice,
    secondaryPrice,
  } = getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)

  const showModal = useContext(ModalContext)
  const openDiscountTsAndCsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const cruiseLineLink = useMemo(() => {
    const cruiseLineCode = offer.cruiseLine?.code!
    if (cruiseLineCode) {
      const queryParams = { cruiseLines: cruiseLineCode }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [offer.cruiseLine?.code])

  const departurePortLink = useMemo(() => {
    if (departurePortPlace?.name) {
      const queryParams = {
        departureNames: departurePortPlace.name.trim(),
        departureIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departurePortPlace])

  const cruisingToLink = useMemo(() => {
    if (hasTheSameStartAndEndPort && departurePortPlace?.name) {
      const queryParams = {
        destinationNames: departurePortPlace.name.trim(),
        destinationIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    if (!hasTheSameStartAndEndPort && destinationPlace?.name) {
      const queryParams = {
        destinationNames: destinationPlace.name.trim(),
        destinationIds: destinationPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departurePortPlace, destinationPlace, hasTheSameStartAndEndPort])

  const shipLink = useMemo(() => {
    const shipName = ship?.name
    if (shipName) {
      const queryParams = { cruiseShips: shipName.trim() }
      if (cruiseLineLink) {
        return `${cruiseLineLink}&${qs.stringify(queryParams)}`
      } else if (ship) {
        return `/search/cruises?${qs.stringify(queryParams)}`
      }
    }

    return null
  }, [cruiseLineLink, ship])

  return (
    <Root className="OfferIntro">
      <TitleArea className="title-area" data-testid="cruise-offer-overview">
        <Group direction="horizontal" gap={8} horizontalAlign="space-between">
          <Heading as="h1" variant="heading2">
            {name}
          </Heading>
          {operatorLogo && <>
            {!cruiseLineLink && <OperatorLogoContainer>
              <OperatorLogoImage
                id={operatorLogo}
                dpr={2}
                quality="eco"
                width={150}
                fit="center"
                alt={operatorName}
                aria-hidden="true"
              />
            </OperatorLogoContainer>}
            {!!cruiseLineLink && <Clickable to={cruiseLineLink}>
              <OperatorLogoContainer>
                <OperatorLogoImage
                  id={operatorLogo}
                  dpr={2}
                  quality="eco"
                  width={150}
                  fit="center"
                  alt={operatorName}
                  aria-hidden="true"
                />
              </OperatorLogoContainer>
            </Clickable>}
          </>}
        </Group>
      </TitleArea>

      <DetailItemsContainer>
        {!!operatorName && <BodyText variant="medium">
          Cruise line:
          {!cruiseLineLink && <b>{operatorName}</b>}
          {!!cruiseLineLink && <TextLinkVendor href={cruiseLineLink}>
            <b>{operatorName}</b>
          </TextLinkVendor>}
        </BodyText>}
        {!!ship && <BodyText variant="medium">
          Cruise ship:
          {!shipLink && <b>{ship.name}</b>}
          {!!shipLink && <TextLinkVendor href={shipLink}>
            <b>{ship.name}</b>
          </TextLinkVendor>}
        </BodyText>}
      </DetailItemsContainer>

      <PromotionArea>
        <CruiseInclusionList
          offer={offer}
          promotionDetails={promotionDetails}
          evergreenInclusions={offer.evergreenInclusions}
          standardInclusions={standardInclusions}
          luxPlusInclusions={luxPlusInclusions}
        />
      </PromotionArea>

      <ShipDescriptionArea>
        {!!offerDescription && <>
          <CSSBreakpoint max="mobile">
            <LargeContentToggle
              maxHeight={100}
              showMoreText="Read more"
              showLessText="Read less"
            >
              <MarkdownRender
                fontSize="medium"
                fontWeight="normal"
                type="compact"
                content={offerDescription}
              />
            </LargeContentToggle>
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet">
            <MarkdownRender
              fontSize="medium"
              fontWeight="normal"
              type="compact"
              content={offerDescription}
            />
          </CSSBreakpoint>
        </>}
      </ShipDescriptionArea>

      <TopBarArea className="top-bar-area">
        <TopBarFlex>
          <BodyTextBlock variant="medium" startIcon={isMobile ? null : <LineMapMarkerIcon />}>
            {hasTheSameStartAndEndPort && <span>
              Starts and ends in
              {!cruisingToLink && <b>&nbsp;{returnPortCity}</b>}
              {!!cruisingToLink && <TextLinkVendor href={cruisingToLink}>
                <b>{returnPortCity}</b>
              </TextLinkVendor>}
            </span>}
            {!hasTheSameStartAndEndPort && <span>
              Starts in
              {!departurePortLink && <b>&nbsp;{departurePortCity}</b>}
              {!!departurePortLink && <TextLinkVendor href={departurePortLink}>
                <b>{departurePortCity}</b>
              </TextLinkVendor>}.
              Ends in
              {!cruisingToLink && <b>&nbsp;{returnPortCity}</b>}
              {!!cruisingToLink && <TextLinkVendor href={cruisingToLink}>
                <b>{returnPortCity}</b>
              </TextLinkVendor>}
            </span>}
          </BodyTextBlock>
        </TopBarFlex>
        <CSSBreakpoint min="tablet">
          <TopBarFlex>
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              {!!shareModalConfig && (
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareWithFriendsButton />
                </ShareModal>
              )}
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
            </Group>
          </TopBarFlex>
        </CSSBreakpoint>
      </TopBarArea>

      {showOverviewArea && <OverviewArea>
        <OverviewPane>
          <Heading variant="heading5">
            Overview
          </Heading>
          <OverviewPaneDetailItemsContainer>
            <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
              {pluralizeToString('night', offer.duration)}
            </BodyTextBlock>
            {!!ports?.length && <BodyTextBlock variant="medium" startIcon={<LineMapIcon />}>
              {pluralizeToString('port', ports.length)}
            </BodyTextBlock>}
            {!!countries?.length && <BodyTextBlock variant="medium" startIcon={<LineGlobeIcon />}>
              {pluralizeToString('country', countries.length)}
            </BodyTextBlock>}
          </OverviewPaneDetailItemsContainer>
          <Hr colour="light-grey" />
          <Group direction="vertical" gap={16}>
            <div>
              {config.DEPOSITS_TAGS_ENABLED && !offer.disableDeposits && <BodyText variant="small">
                {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                  Secure with a {promotionDetails.deposit.formattedValue} deposit
                </Pill>}
                {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                  Deposits available
                </Pill>}
              </BodyText>}

              {lowestOverallPriceDetails && hasValidDepartureDates && <OverviewPanePriceContainer>
                <Group direction="vertical" gap={4}>
                  <div>
                    <PriceRowPriceCaption>From</PriceRowPriceCaption>
                    <PriceRowPrice
                      size="L"
                      price={primaryPrice.total}
                      saleUnit={primaryPrice.type}
                    />
                  </div>
                  {secondaryPrice && <PriceRowCaption>
                    <FormatCurrency value={secondaryPrice.total} />/{secondaryPrice.type} · Twin Share
                  </PriceRowCaption>}
                </Group>
                {discountPills && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={discountPills.price}
                  discountPercentage={discountPills.discountPercentage}
                  onInfoIconClick={openDiscountTsAndCsModal}
                />}
              </OverviewPanePriceContainer>}
            </div>

            <CruiseLoyaltyPoints
              offer={offer}
              calculationType="estimate"
            />
          </Group>

          <Group direction="vertical" gap={12}>
            {hasValidDepartureDates && <TextButton
              kind="primary"
              onClick={showPackageOptions}
              fit="full-width"
            >
              Check availability
            </TextButton>}

            {!hasValidDepartureDates && <Group direction="vertical" gap={12}>
              <BodyText variant="large" weight="bold" align="center">
                This offer has sold out
              </BodyText>
              <TextButton kind="secondary" to="/cruises">Browse all cruise offers</TextButton>
            </Group>}

            <CruiseSupportButton />
          </Group>
        </OverviewPane>
      </OverviewArea>}

      <JourneyArea>
        <BodyText variant="medium" weight="normal">
          Your journey takes you to: {' '}
          <BodyText variant="medium" weight="bold" as="span">
            {ports.join(' - ')}
          </BodyText>
        </BodyText>
      </JourneyArea>

      <ContactUSPArea>
        <ContactUSP />
      </ContactUSPArea>
    </Root>
  )
}

export default CruiseOverview
